import { useEffect, useRef, useState } from 'react';

import type { EdgeRecord, SolverOptions, SolverResult } from '@/features/network/models';
import type { SolveRoutesPhase } from '@/features/routing/solver';
import type { WorkerRequest, WorkerResponse } from '@/features/routing/worker-types';

const EMPTY_RESULT: SolverResult = {
  primary: null,
  backup: null,
  routes: [],
};

export type RouteSolverProgress = {
  phase: SolveRoutesPhase;
  percent: number;
  message: string;
  exploredStates: number;
  foundRoutes: number;
  running: boolean;
};

const EMPTY_PROGRESS: RouteSolverProgress = {
  phase: 'completed',
  percent: 0,
  message: 'Ожидание расчёта',
  exploredStates: 0,
  foundRoutes: 0,
  running: false,
};

export function useRouteSolver(edges: EdgeRecord[], options: SolverOptions) {
  const workerRef = useRef<Worker | null>(null);
  const requestIdRef = useRef(0);
  const [result, setResult] = useState<SolverResult>(EMPTY_RESULT);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<RouteSolverProgress>(EMPTY_PROGRESS);

  useEffect(() => {
    const worker = new Worker(new URL('../../workers/router.worker.ts', import.meta.url), {
      type: 'module',
    });
    workerRef.current = worker;

    const onMessage = (event: MessageEvent<WorkerResponse>) => {
      const data = event.data;
      const { requestId } = data;
      const currentRequestId = String(requestIdRef.current);
      if (requestId !== currentRequestId) return;

      if (data.type === 'progress') {
        const { progress: nextProgress } = data;
        setProgress({
          phase: nextProgress.phase,
          percent: nextProgress.percent,
          message: nextProgress.message,
          exploredStates: nextProgress.exploredStates ?? 0,
          foundRoutes: nextProgress.foundRoutes ?? 0,
          running: nextProgress.phase !== 'completed',
        });
        return;
      }

      setLoading(false);

      if (data.type === 'error') {
        setError(data.error);
        setProgress({
          ...EMPTY_PROGRESS,
          message: 'Расчёт прерван из-за ошибки',
        });
        return;
      }

      if (data.type === 'result') {
        setError(null);
        setProgress((previous) => ({
          phase: 'completed',
          percent: 100,
          message: `Расчёт завершён: ${data.result.routes.length} маршрутов`,
          exploredStates: previous.exploredStates,
          foundRoutes: data.result.routes.length,
          running: false,
        }));
        setResult(data.result ?? EMPTY_RESULT);
      }
    };

    const onError = (event: ErrorEvent) => {
      setLoading(false);
      setError(event.message || 'Ошибка воркера при расчёте маршрута');
      setProgress({
        ...EMPTY_PROGRESS,
        message: 'Ошибка воркера при расчёте',
      });
    };

    const onMessageError = () => {
      setLoading(false);
      setError('Ошибка сериализации сообщения воркера');
      setProgress({
        ...EMPTY_PROGRESS,
        message: 'Ошибка обмена сообщениями с воркером',
      });
    };

    worker.addEventListener('message', onMessage);
    worker.addEventListener('error', onError);
    worker.addEventListener('messageerror', onMessageError);
    return () => {
      worker.removeEventListener('message', onMessage);
      worker.removeEventListener('error', onError);
      worker.removeEventListener('messageerror', onMessageError);
      worker.terminate();
      workerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const worker = workerRef.current;
    if (!worker) return;

    requestIdRef.current += 1;
    const requestId = String(requestIdRef.current);
    setLoading(true);
    setError(null);
    setProgress({
      phase: 'preparing',
      percent: 0,
      message: 'Запуск расчёта',
      exploredStates: 0,
      foundRoutes: 0,
      running: true,
    });

    const payload: WorkerRequest = {
      requestId,
      edges,
      options,
    };

    worker.postMessage(payload);
  }, [edges, options]);

  return { ...result, loading, error, progress };
}
